import WaitingDots from 'components/WaitingDots';
import {Invoice} from 'models/Invoice';
import React, {useState} from 'react';
import {connect} from 'react-redux';
import {Card, CardBody} from 'reactstrap';
import {RootState} from 'store/store';
import {deleteInvoiceContent, editBasicDataInvoice} from "store/actions/Billing";
import {bindActionCreators} from 'redux';
import {DebounceInput} from 'react-debounce-input';
import {useOfficeTranslation} from 'i18n/OfficeTranslation';
import {useInvoiceFormatting} from './useInvoiceFormatting';

interface Props {
    invoice: Invoice;
}

interface Actions {
    deleteInvoiceContent: (i: Invoice, contentId: string) => void;
    editBasicDataInvoice: (d: any) => void;
}



const InvoiceContentCard: React.FC<Props & Actions> = ({ invoice, deleteInvoiceContent, editBasicDataInvoice }) => {
    const {t} = useOfficeTranslation();
    const {currency, quantity} = useInvoiceFormatting();

    const [inEdit, setInEdit] = useState<boolean>(false)

    const deleteContent = (invoice: Invoice, id: string) => {
        deleteInvoiceContent(invoice, id)
    }

    const updateName = (invoice: Invoice, nameInvoice: string) => {

        const data = {
            id: invoice.id,
            name: nameInvoice,
            created: invoice.created,
            deadline: invoice.deadline
        }
        setInEdit(true);
        editBasicDataInvoice(data)
    }

    if (invoice === undefined) {
        return <WaitingDots />
    }

    const presentUom = (amount: number, uom: string): string => {
        if (uom === "min") {
            if (amount > 60) {
                amount = amount / 60;
                uom = "hrs"
            }
            if (amount > 24) {
                amount = amount / 24;
                uom = "days"
            }
            if (amount > 30) {
                amount = amount / 30;
                uom = "mos"
            }
        }
        if (uom === "GBm") {
            uom = "GB"
            amount = amount / (31 * 24 * 60)
        }
        return `${amount.toFixed(1)} ${uom}`;
    }

    const editable = invoice.draft || invoice.quote;
    const taxSubtotals = invoice.CalcTaxSubtotals();
    return <Card className="invoice-content-card">
        <CardBody>
            {editable ? <>
                <div className="form-floating mb-3">
                    <DebounceInput value={invoice.name} minLength={1} debounceTimeout={500}
                        onKeyDown={() => setInEdit(false)} className={inEdit ? "form-control is-valid" : "form-control"}
                        onChange={e => {invoice.name = e.target.value; updateName(invoice, e.target.value);}}
                        placeholder={t("common.name")} />
                    <label>{t("common.name")}</label>
                </div>
            </> : <h4 className="invoice-section-title">{t("invoice.detail.items")}</h4>}
            <div className="invoice-lines-wrapper">
                <table className="table table-borderless align-middle invoice-lines mb-0">
                    <thead className="table-light"><tr>
                        {editable && <th />}
                        <th>{t("invoice.invoiceContentCard.item")}</th><th>{t("common.description")}</th>
                        <th>{t("common.qty")}</th><th>{t("common.price")}</th><th>{t("common.vat")}</th><th>{t("common.total")}</th>
                    </tr></thead>
                    <tbody>{invoice.contents.map(content => <tr key={content.id}>
                        {editable && <td className="invoice-line-delete"><button type="button" className="btn btn-outline-danger btn-sm"
                            aria-label={t("invoice.detail.deleteItem", {name: content.name})}
                            onClick={() => deleteContent(invoice, content.id)}><i className="mdi mdi-delete" aria-hidden="true" /></button></td>}
                        <td className="invoice-line-name">{content.name}</td>
                        <td className="invoice-line-description">{content.description}</td>
                        <td className="invoice-line-quantity">
                            {editable ? presentUom(content.amount, content.uom) : `${quantity(content.amount)} ${content.uom}`}
                            <span className="invoice-line-unit-price"> × {currency(content.price)} · {t("common.vat")} {invoice.vat_reverse_charged ? t("invoice.invoiceContentCard.reverseCharged") : `${quantity(content.tax_rate ?? 0)}%`}</span>
                        </td>
                        <td className="invoice-line-price">{currency(content.price)}</td>
                        <td className="invoice-line-tax"><span className="invoice-line-tax-label">{t("common.vat")} </span>
                            {invoice.vat_reverse_charged ? t("invoice.invoiceContentCard.reverseCharged") : `${(content.tax_rate ?? 0).toFixed(2)}%`}</td>
                        <td className="invoice-line-total">{currency(content.amount * content.price)}</td>
                    </tr>)}</tbody>
                </table>
            </div>
            <dl className="invoice-totals">
                <div><dt>{t("invoice.invoiceContentCard.subtotal")}</dt><dd>{currency(invoice.CalcTotalContents())}</dd></div>
                {invoice.vat_reverse_charged ?
                    <div><dt>{t("invoice.invoiceContentCard.vatReverseCharged")}</dt><dd>{currency(0)}</dd></div> :
                    taxSubtotals.length > 0 ? taxSubtotals.map(subtotal => <div key={subtotal.rate}>
                        <dt>{t("common.vat")} ({quantity(subtotal.rate)}%)</dt><dd>{currency(subtotal.tax_amount)}</dd>
                    </div>) : <div><dt>{t("common.vat")}</dt><dd>{currency(invoice.CalcTaxes())}</dd></div>}
                <div className="invoice-totals-grand"><dt>{t("common.total")}</dt><dd><strong>{currency(invoice.CalcGrandTotal())}</strong></dd></div>
            </dl>
        </CardBody>
    </Card>;
}

const mapStateToProps = (state: RootState) => {
    return {
        invoices: state.Invoices,
    };
};


const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ deleteInvoiceContent, editBasicDataInvoice }, dispatch);
};


export default connect(mapStateToProps, mapDispatchToProps)(InvoiceContentCard);
